import { IHttpClient } from '@aurelia/fetch-client';
import { ApiHelper } from './api-helper';

export interface ApiResponse<T = any> {
  content: T;
  statusCode: number;
}

export abstract class ApiBase {
  private _apiHelper: ApiHelper = new ApiHelper();
  private static _csrfToken: string | null = null;

  constructor(private _http: IHttpClient) {
  }

  public static setCsrfToken(token: string): void {
    ApiBase._csrfToken = token;
  }

  // Must be called after any SPA sign-in so the token reflects the new identity.
  public static async refreshCsrfToken(): Promise<void> {
    const response = await fetch(`${window.location.origin}/api/security/csrf`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) { return; }
    const payload = await response.json() as { requestToken?: string };
    if (payload.requestToken) {
      ApiBase._csrfToken = payload.requestToken;
    }
  }

  protected get(baseUrl: string, parameters?: any): Promise<ApiResponse> {
    const url = this._apiHelper.createUrl(baseUrl, parameters);
    return this._send(url, 'GET');
  }

  protected post(url: string, dto: any): Promise<ApiResponse> {
    return this._send(url, 'POST', dto);
  }

  protected patch(url: string, dto: any): Promise<ApiResponse> {
    return this._send(url, 'PATCH', dto);
  }

  protected delete(url: string): Promise<ApiResponse> {
    return this._send(url, 'DELETE');
  }

  private async _send(url: string, method: string, dto?: any): Promise<ApiResponse> {
    const requiresCsrf = method === 'POST' || method === 'PATCH' || method === 'DELETE' || method === 'PUT';
    if (requiresCsrf && !ApiBase._csrfToken) {
      throw new Error('CSRF token is missing. Initialize CSRF before mutating API calls.');
    }

    const headers: Record<string, string> = {};
    if (requiresCsrf) {
      headers['X-CSRF-TOKEN'] = ApiBase._csrfToken;
    }

    const init: RequestInit = {
      method,
      credentials: 'include',
      headers,
    };

    if (dto instanceof FormData) {
      init.body = dto;
    }
    if (dto !== undefined) {
      init.body = JSON.stringify(dto);
      headers['Content-Type'] = 'application/json';
    }

    const response = await this._http.fetch(url, init);
    const content = await this._parseBody(response);
    const result: ApiResponse = { content, statusCode: response.status };

    if (!response.ok) {
      throw result;
    }

    return result;
  }

  private async _parseBody(response: Response): Promise<any> {
    const text = await response.text();
    if (!text) {
      return null;
    }
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
}
